import { useMutation, useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';

import { getApiErrorMessage } from '@/lib/api/client';
import { getData, postData } from '@/lib/api/client-v2';
import { pushGoogleEvent } from '@/lib/analytics';
import { DATE_FR, DATE_ISO, toDate } from '@/lib/dates';
import { notify } from '@/lib/notify';
import { queryKeys } from '@/lib/query-keys';
import type { ContractDocument, ContractDocumentV2 } from '@/types/api/document';
import { ENDPOINTS } from '@/types/api/endpoints';

/** Pièce v2 ramenée aux noms de la v1 que lisent les écrans. */
const toContractDocument = (document: ContractDocumentV2): ContractDocument => ({
    id: document.id,
    code: document.code,
    libelle: document.title ?? '',
    recu: document.received,
    traite: document.treated,
    commentaire: document.comment,
    // La v1 servait le numéro de pièce en texte (`(int)` puis chaîne) : le sélecteur d'adhésion le compare ainsi.
    nb_piece: String(document.piece_number ?? 0),
    // `treated_date` arrive en jj/mm/aaaa ; les écrans attendent une date ISO (v1 : `Y-m-d H:i:s`).
    dateTraitement: document.treated_date ? format(toDate(document.treated_date, DATE_FR), DATE_ISO) : null,
    dateReception: document.received_at,
    download_link: document.download_url,
});

/**
 * Pièces d'un contrat (`GET /contracts/{id}/documents`).
 *
 * L'ancienne fiche contrat relisait la liste à chaque montage : pas de cache durable.
 */
export const useContractDocuments = (contractId: string | number | null | undefined) =>
    useQuery({
        queryKey: queryKeys.contractDocuments(contractId),
        queryFn: () =>
            getData<ContractDocumentV2[]>(ENDPOINTS.contractDocuments(contractId!)).then((documents) =>
                documents.map(toContractDocument),
            ),
        enabled: contractId !== null && contractId !== undefined && contractId !== '',
        refetchOnMount: 'always',
    });

/** Pièces ayant un code (ancien sélecteur `getDataDocuments`). */
export const getCodedDocuments = (documents: ContractDocument[] | undefined) =>
    documents?.filter((document) => document.code && document.code.trim() !== '');

/** Au moins une pièce attendue non reçue (ancien sélecteur `getIfWaitingDocuments`). */
export const hasWaitingDocuments = (documents: ContractDocument[] | undefined) =>
    (documents ?? []).some((document) => document.recu === false);

export interface RequestDocumentVariables {
    contract: string | number | undefined;
    /** Type de document demandé (`dispositions_particulieres`…), substitué à `{type}` dans la route. */
    type: string;
    /** Libellé du bouton, envoyé à l'analytics. */
    libelle: string;
}

/** Demande d'envoi d'un document contractuel (`POST /document-requests/{type}`, corps `{ contract }`). */
export const useRequestDocument = () =>
    useMutation({
        mutationFn: ({ contract, type, libelle }: RequestDocumentVariables) => {
            pushGoogleEvent({ event: 'click', actionGoogle: 'clickButton', libelle });

            return postData<null>(ENDPOINTS.documentRequest(type), { contract });
        },
        onSuccess: () => {
            void notify({
                html: '<div><p>Votre demande a bien été prise en compte</p></div>',
                icon: 'success',
                title: 'Demande envoyée !',
                confirmButtonText: "J'ai compris",
            });
        },
        onError: (error) => {
            void notify({
                html: "<div><p>Votre demande n'a pas pu être envoyée</p><p>" + getApiErrorMessage(error) + '</p></div>',
                icon: 'error',
                title: 'Attention !',
                confirmButtonText: "J'ai compris",
            });
        },
    });
